import React from 'react';
import './BankLanding.scss';
import {
  STEP_LINK_BANK,
  STEP_MANUAL,
  STEP_BANK_FAILURE,
} from '../AddBank/AddBank';
import Button from '../../Shared/Button/Button';
import lightningBolt from '../../../assets/images/lightning-bolt.png';
import settings, {
  ANY_STOP_TRANSFER,
  MD529_DECOMMISSION_ZERO_STATE,
  NON_ELIGIBLE_AGREEMENTS,
  TRAC_OR_RPS_ONLY,
  CUSTODIAN_ONLY,
} from '../../../settings/settings';
import { useDispatch, useSelector } from 'react-redux';
import { getPersonalInformationData } from '../../../redux/modules/user/PersonalInformation/personalInformation.selectors';
import { setErrorType } from '../../../redux/modules/linkedBanks/linkedBanks.module';
import {
  getEligibleAgreements,
  hasEligibilitySucceed,
  hasIneligibleAgreements,
} from '../../../redux/modules/eligibleAgreements/eligibleAgreements.selectors';
import {
  getHasStopFeatureOnAnyAgreement,
  getIs529CustodianOnly,
  getRPSOrTRAConly,
  getHasInheritedIRAAgreementsOnly,
  getCollegeTypeCount,
} from '../../../redux/modules/productTypes/productType.selectors';
import { isLockout } from '../../../utils/banking/bank-utils';

export const BankLanding = ({
  customerWebViewEnabled,
  dispatchPageLoad,
  eVerification: { attempts: everifyAttempts },
  linkedBanks: { attempts: plaidAttempts },
  setBankProgress,
  setUserPath,
  isSalesforceRep,
}) => {
  const dispatch = useDispatch();

  const ineligibleAgreements = useSelector(hasIneligibleAgreements);
  const hasInheritedIRAAgreementsOnly = useSelector(
    getHasInheritedIRAAgreementsOnly
  );
  const hasStopFeatureOnAnyAgreement = useSelector(
    getHasStopFeatureOnAnyAgreement
  );
  const is529CustodianOnly = useSelector(getIs529CustodianOnly);
  const isRPSOrTRAConly = useSelector(getRPSOrTRAConly);
  const collegeTypeCount = useSelector(getCollegeTypeCount);
  const eligibleAgreements = useSelector(getEligibleAgreements);
  const didIneligibleAgreementsSucceed = useSelector(hasEligibilitySucceed);
  function linkBankClick() {
    setUserPath('LINK');
    if (
      collegeTypeCount === 0 &&
      eligibleAgreements.length === 0 &&
      didIneligibleAgreementsSucceed
    ) {
      setBankProgress(STEP_LINK_BANK);
      dispatch(setErrorType(MD529_DECOMMISSION_ZERO_STATE));
    } else if (
      (ineligibleAgreements && didIneligibleAgreementsSucceed) ||
      hasInheritedIRAAgreementsOnly
    ) {
      setBankProgress(STEP_LINK_BANK);
      dispatch(setErrorType(NON_ELIGIBLE_AGREEMENTS));
    } else if (hasStopFeatureOnAnyAgreement) {
      setBankProgress(STEP_LINK_BANK);
      dispatch(setErrorType(ANY_STOP_TRANSFER));
    } else if (is529CustodianOnly) {
      setBankProgress(STEP_LINK_BANK);
      dispatch(setErrorType(CUSTODIAN_ONLY));
    } else if (isRPSOrTRAConly) {
      setBankProgress(STEP_LINK_BANK);
      dispatch(setErrorType(TRAC_OR_RPS_ONLY));
    } else if (!isLockout('LINK', everifyAttempts, plaidAttempts)) {
      setBankProgress(STEP_LINK_BANK);
      dispatchPageLoad('Choose Bank');
    } else {
      setBankProgress(STEP_BANK_FAILURE);
    }
  }

  function manualBankClick() {
    setUserPath('MANUAL');
    if (
      collegeTypeCount === 0 &&
      eligibleAgreements.length === 0 &&
      didIneligibleAgreementsSucceed
    ) {
      setBankProgress(STEP_MANUAL);
      dispatch(setErrorType(MD529_DECOMMISSION_ZERO_STATE));
    } else if (
      (ineligibleAgreements && didIneligibleAgreementsSucceed) ||
      hasInheritedIRAAgreementsOnly
    ) {
      setBankProgress(STEP_MANUAL);
      dispatch(setErrorType(NON_ELIGIBLE_AGREEMENTS));
    } else if (hasStopFeatureOnAnyAgreement) {
      setBankProgress(STEP_MANUAL);
      dispatch(setErrorType(ANY_STOP_TRANSFER));
    } else if (is529CustodianOnly) {
      setBankProgress(STEP_MANUAL);
      dispatch(setErrorType(CUSTODIAN_ONLY));
    } else if (isRPSOrTRAConly) {
      setBankProgress(STEP_MANUAL);
      dispatch(setErrorType(TRAC_OR_RPS_ONLY));
    } else if (!isLockout('MANUAL', everifyAttempts, plaidAttempts)) {
      setBankProgress(STEP_MANUAL);
    } else {
      setBankProgress(STEP_BANK_FAILURE);
    }
  }
  const userInformation = useSelector(getPersonalInformationData);
  const plaidUser = {
    override_accounts: [
      {
        type: 'depository',
        subtype: 'savings',
        identity: {
          names: [userInformation?.fullName],
          addresses: [
            {
              primary: true,
              data: {
                city: userInformation?.primaryAddress?.city,
                region: userInformation?.primaryAddress?.state,
                street: userInformation?.primaryAddress?.street1,
                postal_code: userInformation?.primaryAddress?.zip,
                country: userInformation?.primaryAddress?.countryCode,
              },
            },
          ],
        },
      },
    ],
  };
  return (
    <div className="bank-landing">
      <div className="bank-landing-link-bank-option">
        <div className="bank-landing-link-bank-heading">
          <span className="bank-landing-heading-bold">Link your bank</span>
        </div>
        <div className="bank-landing-below-info">
          Your bank account will stay linked to speed up future transactions.
          You can unlink it at any time.
        </div>
        <div className="bank-landing-info-points">
          <div className="bank-landing-info-point first">
            <div className="bank-landing-ico_manual">
              <div className="icon-image-wrapper">
                <img alt="lightningBolt" src={lightningBolt} />
              </div>
            </div>
            <div className="bank-landing-below-info">
              <div className="bank-landing-info-heading">
                Link my bank account with Plaid
              </div>
              <p>
                Use your bank sign-in information to securely access your
                account in about 30 seconds or less.
              </p>
              <p>By proceeding with the 'link automatically' option:</p>
              <ul>
                <li>
                  You will be directed to a third-party, Plaid, to securely
                  enter your bank account login credentials, which will be used
                  by Plaid on a one-time basis to obtain your bank account
                  details, such as the name(s) of the account owner(s) and
                  account number. Click to view&nbsp;
                  <a
                    target="_blank"
                    href="https://plaid.com/legal/ios/?utm_source=google&utm_medium=search&utm_campaign=Search_G_NonBrand_DSA&utm_content=Plaid.com&utm_term=&utm_creative=499104059460&gclid=EAIaIQobChMIwMG53cuF-QIVjf_jBx2zfQ4FEAAYASAAEgLEq_D_BwE"
                    rel="noreferrer"
                  >
                    Plaid’s End User Privacy Policy
                  </a>
                </li>
                <li>
                  Plaid will provide your bank account details (but not your
                  login credentials) to T. Rowe Price for use in facilitating
                  your transaction request. As detailed in&nbsp;
                  <a
                    target="blank"
                    href="https://www.troweprice.com/enterprise/legal/us/en/privacy-notices.html#disclose"
                    rel="noreferrer"
                  >
                    T. Rowe Price's Privacy Notice
                  </a>
                  , we may provide data about you, including your bank account
                  details we obtain from Plaid, to affiliates, technology
                  service providers, and other third parties.
                </li>
                <li>
                  Note: Some banks will use tokenized account and/or routing
                  numbers (not your actual account number) to protect your bank
                  information so you may see an account number that is not
                  yours. Please reach out to your bank with any questions
                  regarding tokenized account or routing numbers.
                </li>
              </ul>
              <Button
                id="link-my-bank-button"
                className={`btn-trp-primary-large bank-landing-link-button link-auto-btn`}
                buttonType="secondary"
                disabled={customerWebViewEnabled || isSalesforceRep}
                onClick={linkBankClick}
              >
                Link automatically
              </Button>
              or enter your bank account details and verify a few security
              questions to securely access your account in a few minutes.
              <br />
              <p className={'link-maually-btn'}>
                <a
                  id="enter-details-manual-bank-link"
                  href="#"
                  disabled={customerWebViewEnabled}
                  onClick={manualBankClick}
                >
                  Link manually
                </a>
              </p>
            </div>
          </div>
        </div>
        <p>4051276</p>
      </div>
      {!settings.isProd && console.log('user', JSON.stringify(plaidUser))}
    </div>
  );
};

export default BankLanding;
